import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Target, CheckCircle2, Trophy, Sparkles, Pencil } from "lucide-react";
import ZielplanungsAssistentModal, { WIZARD_STORAGE_KEY } from "@/components/ZielplanungsAssistentModal";
import CelebrationModal from "@/components/CelebrationModal";

import { userStorage } from '../utils/userStorage';
const API = process.env.REACT_APP_BACKEND_URL;

export default function GoalPlanningView({ onProgressChange, milestoneModalOpen, onMilestoneModalClose, onNavigateTo }) {
  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [showCongratulations, setShowCongratulations] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [pendingWizardOpen, setPendingWizardOpen] = useState(false);
  const [wizardData, setWizardData] = useState(() => {
    try { return JSON.parse(userStorage.getItem(WIZARD_STORAGE_KEY)); } catch { return null; }
  });
  // Verfolgt den vorherigen Fortschritt, um 100%-Übergang zu erkennen
  const prevProgressRef = useRef(null);

  // Wizard-Sync: Bei burningteam-wizard-sync neu laden (z.B. nach WeekView-Abhaken)
  useEffect(() => {
    const refresh = () => {
      try { setWizardData(JSON.parse(userStorage.getItem(WIZARD_STORAGE_KEY))); } catch { setWizardData(null); }
    };
    window.addEventListener("burningteam-wizard-sync", refresh);
    return () => window.removeEventListener("burningteam-wizard-sync", refresh);
  }, []);

  // Aktuelle ISO-Wochennummer als Key (z.B. "2026-W8")
  const getCurrentWeekKey = () => {
    const now = new Date();
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNum = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return `${d.getUTCFullYear()}-W${weekNum}`;
  };

  // Wochenziel im Modal umschalten + Kaskadierung
  const toggleWizardWeek = () => {
    try {
      const data = JSON.parse(userStorage.getItem(WIZARD_STORAGE_KEY));
      const weekKey = getCurrentWeekKey();
      if (!data?.weekGoals?.[weekKey]) return;
      const updated = JSON.parse(JSON.stringify(data));
      const entry = updated.weekGoals[weekKey];
      entry.done = !entry.done;
      entry.progress = entry.done ? entry.number : 0;
      // Kaskadierung: Monat prüfen
      const monthKey = entry.monthKey;
      if (monthKey && updated.monthGoals?.[monthKey]) {
        const weeksInMonth = Object.entries(updated.weekGoals).filter(([, v]) => v.monthKey === monthKey);
        updated.monthGoals[monthKey].done = weeksInMonth.every(([, v]) => v.done);
      }
      // Kaskadierung: Jahr prüfen
      if (updated.yearGoal) {
        updated.yearGoal.done = Object.values(updated.monthGoals || {}).every(m => m.done);
      }
      userStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(updated));
      setWizardData(updated);
      window.dispatchEvent(new CustomEvent("burningteam-wizard-sync"));
      // Celebration wird via prevProgressRef-useEffect ausgelöst
    } catch {}
  };

  // Neues leeres Ziel definieren
  const handleNewGoal = () => {
    if (!window.confirm("Wirklich ein neues Ziel definieren? Die aktuellen Einträge werden durch das Speichern überschrieben.")) return;
    // Wizard-Daten (Jahres-/Monats-/Wochenziele) zurücksetzen
    userStorage.removeItem(WIZARD_STORAGE_KEY);
    setWizardData(null);
    window.dispatchEvent(new CustomEvent("burningteam-wizard-sync"));
    setForm({
      title: "",
      deadline: "",
      habits: [""],
      motivation: "",
      milestones: [{ t: "", d: false }],
      obstacles: [{ p: "", s: "" }, { p: "", s: "" }, { p: "", s: "" }],
      successes: []
    });
    setIsEditing(true);
  };

  // Jahresziel abhaken: alle Wochen/Monate + ggf. Wizard-Meilenstein setzen
  const toggleWizardYearGoal = async () => {
    try {
      const data = JSON.parse(userStorage.getItem(WIZARD_STORAGE_KEY));
      if (!data?.yearGoal) return;
      const updated = JSON.parse(JSON.stringify(data));
      const newDone = !updated.yearGoal.done;
      updated.yearGoal.done = newDone;
      Object.keys(updated.monthGoals || {}).forEach(k => { updated.monthGoals[k].done = newDone; });
      Object.keys(updated.weekGoals || {}).forEach(k => {
        updated.weekGoals[k].done = newDone;
        updated.weekGoals[k].progress = newDone ? (updated.weekGoals[k].number || 0) : 0;
      });
      userStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(updated));
      setWizardData(updated);
      window.dispatchEvent(new CustomEvent("burningteam-wizard-sync"));

      // Wizard-Meilenstein in der Liste synchronisieren
      if (updated.wizardMilestoneText && goal) {
        const token = localStorage.getItem("token");
        const newMs = form.milestones.map(m =>
          m.t === updated.wizardMilestoneText ? { ...m, d: newDone } : m
        );
        setForm(prev => ({ ...prev, milestones: newMs }));
        const msIdx = form.milestones.findIndex(m => m.t === updated.wizardMilestoneText);
        if (msIdx !== -1) {
          try {
            await axios.patch(`${API}/api/goals/${goal.id}/milestone/${msIdx}`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch {}
        }
      }

      // Celebration wird via prevProgressRef-useEffect ausgelöst (nur beim Abhaken)
    } catch (e) { console.error(e); }
  };

  // Sync milestone modal with parent control
  useEffect(() => {
    if (milestoneModalOpen) {
      setShowMilestoneModal(true);
    }
  }, [milestoneModalOpen]);
  
  // Notify parent when modal closes
  useEffect(() => {
    if (!showMilestoneModal && onMilestoneModalClose) {
      onMilestoneModalClose();
    }
  }, [showMilestoneModal, onMilestoneModalClose]);
  
  const [form, setForm] = useState({
    title: "",
    deadline: "",
    habits: [""],
    motivation: "",
    milestones: [{ t: "", d: false }],
    obstacles: [{ p: "", s: "" }, { p: "", s: "" }, { p: "", s: "" }],
    successes: []
  });

  useEffect(() => {
    loadGoal();
    // Clean up malformed appointments on mount
    cleanupMalformedAppointments();
  }, []);

  const cleanupMalformedAppointments = () => {
    const STORAGE_KEY = 'zielplanung_appointments';
    const stored = userStorage.getItem(STORAGE_KEY);
    if (stored) {
      let appointments = JSON.parse(stored);
      const before = appointments.length;
      appointments = appointments.filter(apt => apt.startTime && apt.endTime);
      if (appointments.length !== before) {
        userStorage.setItem(STORAGE_KEY, JSON.stringify(appointments));
        console.log(`Cleaned up ${before - appointments.length} malformed appointments`);
      }
    }
  };

  const loadGoal = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/goals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.length > 0) {
        const g = res.data[0];
        setGoal(g);
        setForm({
          title: g.title || "",
          deadline: g.deadline || "",
          habits: g.habits?.length > 0 ? g.habits : [""],
          motivation: g.motivation || "",
          milestones: g.milestones?.length > 0 ? g.milestones : [{ t: "", d: false }],
          obstacles: g.obstacles?.length >= 3 ? g.obstacles : [{ p: "", s: "" }, { p: "", s: "" }, { p: "", s: "" }],
          successes: g.successes || []
        });
        setIsEditing(false);
      } else {
        setIsEditing(true); // New goal - start in edit mode
      }
    } catch (err) {
      console.error("Error loading goal:", err);
      setIsEditing(true);
    } finally {
      setLoading(false);
    }
  };

  // Berechnet den Wizard-Fortschrittsanteil (0–1) basierend auf Wochenzielen oder binary
  const getWizardFraction = () => {
    if (!wizardData) return 0;
    if (wizardData.yearGoal?.done) return 1;
    if (wizardData.weekGoals) {
      const totalWeeks = Object.keys(wizardData.weekGoals).length || 52;
      const doneWeeks = Object.values(wizardData.weekGoals).filter(w => w.done).length;
      return doneWeeks / totalWeeks;
    }
    return 0;
  };

  // Berechnet Gesamtfortschritt aus einem expliziten wizardData-Objekt (kein State-Delay)
  const getProgressWith = (wData) => {
    const validMs = form.milestones.filter(m => m.t.trim());
    const wizFrac = (() => {
      if (!wData) return 0;
      if (wData.yearGoal?.done) return 1;
      if (wData.weekGoals) {
        const t = Object.keys(wData.weekGoals).length || 52;
        const d = Object.values(wData.weekGoals).filter(w => w.done).length;
        return d / t;
      }
      return 0;
    })();
    if (wData?.wizardMilestoneText) {
      const others = validMs.filter(m => m.t !== wData.wizardMilestoneText);
      if (!others.length) return Math.round(wizFrac * 100);
      const otherFrac = others.filter(m => m.d).length / others.length;
      return Math.round((wizFrac * 0.4 + otherFrac * 0.6) * 100);
    }
    const msFrac = validMs.length ? validMs.filter(m => m.d).length / validMs.length : 0;
    if (!wData?.weekGoals) return Math.round(msFrac * 100);
    return Math.round((wizFrac * 0.4 + msFrac * 0.6) * 100);
  };

  const calculateProgress = () => {
    const validMilestones = form.milestones.filter(m => m.t.trim());

    // Mit Wizard-Meilenstein: Dieser = 40%, Rest = 60% (kein anderer → 100%)
    // Wochenziele fließen anteilig in die 40% ein
    if (wizardData?.wizardMilestoneText) {
      const others = validMilestones.filter(m => m.t !== wizardData.wizardMilestoneText);
      const wizFraction = getWizardFraction();
      if (others.length === 0) {
        return Math.round(wizFraction * 100);
      }
      const otherFrac = others.filter(m => m.d).length / others.length;
      return Math.round((wizFraction * 0.4 + otherFrac * 0.6) * 100);
    }

    const milestoneFraction = validMilestones.length === 0 ? 0
      : validMilestones.filter(m => m.d).length / validMilestones.length;

    if (!wizardData?.weekGoals) {
      // Kein Wizard: 100% Meilensteine
      return Math.round(milestoneFraction * 100);
    }

    // Mit Wizard (Wochen): 40% Wochenziele + 60% Meilensteine
    const totalWeeks = Object.keys(wizardData.weekGoals).length || 52;
    const doneWeeks = Object.values(wizardData.weekGoals).filter(w => w.done).length;
    const wizardFraction = wizardData.yearGoal?.done ? 1 : doneWeeks / totalWeeks;

    return Math.round((wizardFraction * 0.4 + milestoneFraction * 0.6) * 100);
  };

  // Update parent mit Progress + 100%-Übergang erkennen (inkl. WeekView-Trigger)
  useEffect(() => {
    const prog = calculateProgress();
    if (onProgressChange) onProgressChange(prog);
    // Feier auslösen wenn Fortschritt auf 100% springt (nicht beim ersten Mount)
    if (prevProgressRef.current !== null && prevProgressRef.current < 100 && prog === 100) {
      setShowCongratulations(true);
    }
    prevProgressRef.current = prog;
  }, [form.milestones, wizardData]); // eslint-disable-line

  // Generate goal deadline appointment for calendar (only the deadline day)
  const generateGoalDeadlineAppointment = (goalId, goalTitle, deadline) => {
    if (!deadline) return null;
    
    return {
      id: `goal-deadline-${goalId}`,
      title: `🏆 Zieltag: ${goalTitle}`,
      date: deadline,
      startTime: "09:00",
      endTime: "10:00",
      isGoalDeadline: true,
      goalId: goalId
    };
  };

  // Update calendar with goal deadline
  const updateCalendarWithGoalDeadline = (goalId, goalTitle, newDeadline) => {
    const STORAGE_KEY = 'zielplanung_appointments';
    const stored = userStorage.getItem(STORAGE_KEY);
    let appointments = stored ? JSON.parse(stored) : [];
    
    // Clean up any malformed appointments (missing startTime/endTime)
    appointments = appointments.filter(apt => apt.startTime && apt.endTime);
    
    // Remove old goal appointments - check by goalId OR by id pattern
    appointments = appointments.filter(apt => {
      // Remove if it's a goal deadline for THIS goal (by goalId)
      if (apt.goalId === goalId) return false;
      // Remove if id starts with goal-deadline-{goalId} (in case properties were modified)
      if (apt.id && apt.id.startsWith(`goal-deadline-${goalId}`)) return false;
      // Remove old goal reminders too
      if (apt.isGoalReminder && apt.goalId === goalId) return false;
      // Keep everything else
      return true;
    });
    
    // Add deadline day if there's a deadline
    if (newDeadline) {
      const deadlineAppointment = generateGoalDeadlineAppointment(goalId, goalTitle, newDeadline);
      if (deadlineAppointment) {
        appointments.push(deadlineAppointment);
        console.log("Added goal deadline to calendar:", deadlineAppointment);
      }
    }
    
    // Save back to localStorage
    userStorage.setItem(STORAGE_KEY, JSON.stringify(appointments));
    console.log("Saved appointments to localStorage:", appointments.length);
    
    return appointments;
  };

  // Save goal reminders to backend for notifications
  const saveGoalRemindersToBackend = async (goalId, goalTitle, deadline) => {
    if (!deadline) return;
    
    const token = localStorage.getItem("token");
    if (!token) return;
    
    try {
      // Calculate monthly reminder dates
      const reminders = [];
      const deadlineDate = new Date(deadline);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const targetDay = deadlineDate.getDate();
      let currentDate = new Date(today.getFullYear(), today.getMonth(), targetDay);
      
      if (currentDate < today) {
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
      
      while (currentDate < deadlineDate) {
        const monthsRemaining = (deadlineDate.getFullYear() - currentDate.getFullYear()) * 12 
          + (deadlineDate.getMonth() - currentDate.getMonth());
        
        if (monthsRemaining > 0) {
          reminders.push({
            date: currentDate.toISOString().split('T')[0],
            months_remaining: monthsRemaining
          });
        }
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
      
      // Save to backend
      await axios.put(`${API}/api/goals/${goalId}`, {
        goal_reminders: reminders,
        deadline: deadline
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error("Error saving goal reminders:", err);
    }
  };

  const saveGoal = async () => {
    if (!form.title.trim()) {
      toast.error("Bitte gib ein Ziel ein");
      return;
    }

    const token = localStorage.getItem("token");
    
    // Calculate goal reminders for backend
    const goalReminders = [];
    if (form.deadline) {
      const deadlineDate = new Date(form.deadline);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const targetDay = deadlineDate.getDate();
      let currentDate = new Date(today.getFullYear(), today.getMonth(), targetDay);
      
      if (currentDate < today) {
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
      
      while (currentDate < deadlineDate) {
        const monthsRemaining = (deadlineDate.getFullYear() - currentDate.getFullYear()) * 12 
          + (deadlineDate.getMonth() - currentDate.getMonth());
        
        if (monthsRemaining > 0) {
          goalReminders.push({
            date: currentDate.toISOString().split('T')[0],
            months_remaining: monthsRemaining
          });
        }
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
    }
    
    const goalData = {
      ...form,
      habits: form.habits.filter(h => h.trim()),
      milestones: form.milestones.filter(m => m.t.trim()),
      obstacles: form.obstacles.filter(o => o.p.trim() || o.s.trim()),
      goal_reminders: goalReminders
    };

    try {
      let savedGoal;
      
      if (goal) {
        await axios.put(`${API}/api/goals/${goal.id}`, goalData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        savedGoal = { ...goal, ...goalData };
      } else {
        const res = await axios.post(`${API}/api/goals`, goalData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        savedGoal = res.data;
        setGoal(res.data);
      }
      
      // Calendar update – separat abfangen damit kein Speicher-Fehler entsteht
      try {
        if (form.deadline) {
          updateCalendarWithGoalDeadline(savedGoal.id, form.title, form.deadline);
          toast.success("Ziel gespeichert! Zieltag im Kalender markiert.");
        } else {
          toast.success("Ziel gespeichert!");
        }
      } catch (calErr) {
        console.error("Calendar update error (goal saved):", calErr);
        toast.success("Ziel gespeichert!");
      }
      
      setIsEditing(false);
      loadGoal();
      // Wizard nach erfolgreichem Speichern anzeigen
      setPendingWizardOpen(true);
    } catch (err) {
      console.error("Save goal error:", err);
      toast.error("Fehler beim Speichern: " + (err.response?.data?.detail || err.message || "Unbekannter Fehler"));
    }
  };

  // Wizard öffnen nachdem Goal geladen wurde
  useEffect(() => {
    if (pendingWizardOpen && !loading) {
      setShowWizard(true);
      setPendingWizardOpen(false);
    }
  }, [pendingWizardOpen, loading]);

  const handleWizardClose = async (saved) => {
    setShowWizard(false);
    if (saved) {
      try {
        const d = JSON.parse(userStorage.getItem(WIZARD_STORAGE_KEY));
        setWizardData(d);

        // Wizard-Meilenstein: umbenennen oder neu hinzufügen
        if (d?.wizardMilestoneText && goal) {
          const token = localStorage.getItem("token");
          let newMilestones = [...form.milestones];

          if (d.wizardMilestoneOrigText) {
            // Vorhandener Meilenstein wurde per Chip gewählt → umbenennen
            const idx = newMilestones.findIndex(m => m.t === d.wizardMilestoneOrigText);
            if (idx !== -1) {
              newMilestones[idx] = { ...newMilestones[idx], t: d.wizardMilestoneText };
            } else {
              // Nicht gefunden (z.B. nach Neuplanung) → trotzdem hinzufügen wenn noch nicht da
              if (!newMilestones.some(m => m.t === d.wizardMilestoneText)) {
                newMilestones.push({ t: d.wizardMilestoneText, d: false });
              }
            }
          } else {
            // Freier Text → als neuen Meilenstein hinzufügen (falls noch nicht vorhanden)
            if (!newMilestones.some(m => m.t === d.wizardMilestoneText)) {
              newMilestones.push({ t: d.wizardMilestoneText, d: false });
            }
          }

          setForm(prev => ({ ...prev, milestones: newMilestones }));
          try {
            await axios.put(`${API}/api/goals/${goal.id}`, {
              title: form.title,
              deadline: form.deadline,
              habits: form.habits.filter(h => h.trim()),
              milestones: newMilestones.filter(m => m.t.trim()),
              obstacles: form.obstacles,
              motivation: form.motivation,
              successes: form.successes,
            }, { headers: { Authorization: `Bearer ${token}` } });
          } catch (err) {
            console.error("Fehler beim Speichern des Wizard-Meilensteins:", err);
          }
        }
      } catch (e) {
        console.error(e);
      }
      window.dispatchEvent(new CustomEvent("burningteam-wizard-sync"));
    }
  };

  const toggleMilestone = async (index) => {
    const newMilestones = [...form.milestones];
    newMilestones[index].d = !newMilestones[index].d;
    setForm({ ...form, milestones: newMilestones });

    // Wenn Wizard-Meilenstein: Jahresziel + alle Wochen synchronisieren
    const toggledMs = newMilestones[index];
    if (wizardData?.wizardMilestoneText && toggledMs.t === wizardData.wizardMilestoneText) {
      try {
        const data = JSON.parse(userStorage.getItem(WIZARD_STORAGE_KEY));
        if (data?.yearGoal !== undefined) {
          const updated = JSON.parse(JSON.stringify(data));
          updated.yearGoal.done = toggledMs.d;
          if (toggledMs.d) {
            Object.keys(updated.monthGoals || {}).forEach(k => { updated.monthGoals[k].done = true; });
            Object.keys(updated.weekGoals || {}).forEach(k => {
              updated.weekGoals[k].done = true;
              updated.weekGoals[k].progress = updated.weekGoals[k].number || 0;
            });
          } else {
            Object.keys(updated.monthGoals || {}).forEach(k => { updated.monthGoals[k].done = false; });
            Object.keys(updated.weekGoals || {}).forEach(k => {
              updated.weekGoals[k].done = false;
              updated.weekGoals[k].progress = 0;
            });
          }
          userStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(updated));
          setWizardData(updated);
          window.dispatchEvent(new CustomEvent("burningteam-wizard-sync"));
        }
      } catch {}
    }

    // Glückwunsch nur wenn gesamter Fortschritt 100% erreicht (inkl. Wizard)
    const validMs = newMilestones.filter(m => m.t.trim());
    let newProgress;
    if (wizardData?.wizardMilestoneText) {
      const others = validMs.filter(m => m.t !== wizardData.wizardMilestoneText);
      const wizFrac = getWizardFraction();
      if (others.length === 0) {
        newProgress = Math.round(wizFrac * 100);
      } else {
        const otherFrac = others.filter(m => m.d).length / others.length;
        newProgress = Math.round((wizFrac * 0.4 + otherFrac * 0.6) * 100);
      }
    } else if (wizardData?.weekGoals) {
      const totalWeeks = Object.keys(wizardData.weekGoals).length || 52;
      const doneWeeks = Object.values(wizardData.weekGoals).filter(w => w.done).length;
      const wizFrac = wizardData.yearGoal?.done ? 1 : doneWeeks / totalWeeks;
      const msFrac = validMs.length === 0 ? 0 : validMs.filter(m => m.d).length / validMs.length;
      newProgress = Math.round((wizFrac * 0.4 + msFrac * 0.6) * 100);
    } else {
      newProgress = Math.round((validMs.length === 0 ? 0 : validMs.filter(m => m.d).length / validMs.length) * 100);
    }

    if (newProgress === 100) {
      setShowMilestoneModal(false);
      // Celebration wird via useEffect ausgelöst (prevProgressRef-Vergleich)
    }

    // Auto-save milestone toggle
    if (goal) {
      try {
        const token = localStorage.getItem("token");
        await axios.patch(`${API}/api/goals/${goal.id}/milestone/${index}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        console.error("Error toggling milestone:", err);
      }
    }
  };

  const addHabit = () => {
    if (form.habits.length < 10) {
      setForm({ ...form, habits: [...form.habits, ""] });
    }
  };

  const addMilestone = () => {
    if (form.milestones.length < 10) {
      setForm({ ...form, milestones: [...form.milestones, { t: "", d: false }] });
    }
  };

  const addSuccess = () => {
    setForm({ ...form, successes: [...form.successes, ""] });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const progress = calculateProgress();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Wizard */}
      <ZielplanungsAssistentModal
        open={showWizard}
        onClose={handleWizardClose}
        goalTitle={form.title}
        milestones={form.milestones}
        existingData={wizardData}
      />

      {/* Wizard-Status Banner */}
      {wizardData && !isEditing && (
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target size={16} className="text-emerald-600" />
            <div>
              <p className="text-xs font-semibold text-emerald-700">Zielplanung aktiv</p>
              <p className="text-xs text-emerald-600">
                {wizardData.yearGoal?.number} {wizardData.yearGoal?.text}
              </p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => setShowWizard(true)}
            className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-7 px-2">
            Neu planen
          </Button>
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-slate-200 p-4">
          <div className="flex items-center gap-2 text-emerald-800 font-semibold">
            <Target className="text-emerald-600" size={20} />
            {isEditing ? (goal ? "Ziel bearbeiten" : "Neues Ziel") : "Mein Ziel"}
          </div>
        </div>
        
        <div className="p-4 space-y-5">
          {/* Ziel + Deadline */}
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Dein Ziel</label>
              {isEditing ? (
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Was möchtest du erreichen?"
                  className="mt-1 border-slate-200"
                />
              ) : (
                <p className="mt-1 text-xl font-bold text-emerald-800">{form.title || "-"}</p>
              )}
            </div>
            <div>
              <label className="text-base font-bold text-emerald-700">Bis wann?</label>
              {isEditing ? (
                <Input
                  type="date"
                  value={form.deadline}
                  onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                  className="mt-1 border-emerald-300 border-2 text-base font-semibold"
                />
              ) : (
                <p className="mt-1 text-lg font-semibold text-slate-800">{formatDate(form.deadline)}</p>
              )}
            </div>
          </div>

          {/* Gewohnheiten */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">
                Gewohnheiten & Routinen, die mich unterstützen
              </label>
              {isEditing && (
                <Button variant="ghost" size="sm" onClick={addHabit} disabled={form.habits.length >= 10} className="text-emerald-600 h-7 px-2">
                  <Plus size={14} />
                </Button>
              )}
            </div>
            {isEditing ? (
              <div className="space-y-2">
                {form.habits.map((habit, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      value={habit}
                      onChange={(e) => {
                        const newHabits = [...form.habits];
                        newHabits[i] = e.target.value;
                        setForm({ ...form, habits: newHabits });
                      }}
                      placeholder={`Gewohnheit ${i + 1}`}
                      className="border-slate-200 text-sm"
                    />
                    {form.habits.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, habits: form.habits.filter((_, idx) => idx !== i) })} className="text-red-500 h-9 px-2">
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <ul className="list-disc list-inside text-slate-700 space-y-1">
                {form.habits.filter(h => h.trim()).map((habit, i) => (
                  <li key={i}>{habit}</li>
                ))}
                {form.habits.filter(h => h.trim()).length === 0 && <p className="text-slate-400 italic">-</p>}
              </ul>
            )}
            {isEditing && (
              <Button variant="outline" size="sm" className="mt-2 text-blue-600 border-blue-200 text-xs" onClick={() => onNavigateTo && onNavigateTo('tracking')}>
                → Zu Habit Tracker
              </Button>
            )}
          </div>

          {/* Motivation */}
          <div>
            <label className="text-sm font-medium text-slate-700">
              Motivation: Warum willst du dein Ziel erreichen?
            </label>
            {isEditing ? (
              <Textarea
                value={form.motivation}
                onChange={(e) => setForm({ ...form, motivation: e.target.value })}
                placeholder="Was treibt dich an?"
                className="mt-1 border-slate-200 min-h-[60px] text-sm"
              />
            ) : (
              <p className="mt-1 text-slate-700">{form.motivation || <span className="text-slate-400 italic">-</span>}</p>
            )}
          </div>

          {/* Meilensteine - Checkboxen immer klickbar */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">
                Wichtige Meilensteine, Ergebnisse, Schritte
              </label>
              {isEditing && (
                <Button variant="ghost" size="sm" onClick={addMilestone} disabled={form.milestones.length >= 10} className="text-emerald-600 h-7 px-2">
                  <Plus size={14} />
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {(isEditing
                ? form.milestones
                : form.milestones.filter(m => m.t.trim() && m.t !== wizardData?.wizardMilestoneText)
              ).map((milestone, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={milestone.d}
                    onChange={() => toggleMilestone(isEditing ? i : form.milestones.findIndex(m => m.t === milestone.t))}
                    className="w-5 h-5 accent-emerald-500 rounded cursor-pointer"
                  />
                  {isEditing ? (
                    <>
                      <Input
                        value={milestone.t}
                        onChange={(e) => {
                          const newMilestones = [...form.milestones];
                          newMilestones[i].t = e.target.value;
                          setForm({ ...form, milestones: newMilestones });
                        }}
                        placeholder={`Meilenstein ${i + 1}`}
                        className={`border-slate-200 flex-1 text-sm ${milestone.d ? "line-through text-slate-400" : ""}`}
                      />
                      {form.milestones.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, milestones: form.milestones.filter((_, idx) => idx !== i) })} className="text-red-500 h-9 px-2">
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </>
                  ) : (
                    <span className={`flex-1 ${milestone.d ? "line-through text-slate-400" : "text-slate-700"}`}>
                      {milestone.t}
                    </span>
                  )}
                </div>
              ))}
              {!isEditing && form.milestones.filter(m => m.t.trim()).length === 0 && (
                <p className="text-slate-400 italic">-</p>
              )}
            </div>
            {isEditing && (
              <p className="text-xs text-slate-500 mt-1">
                {wizardData?.wizardMilestoneText
                  ? `Wizard-Meilenstein = 40% EXP · andere = ${form.milestones.filter(m => m.t.trim()).length > 1 ? Math.round(60 / (form.milestones.filter(m => m.t.trim()).length - 1)) : 60}% geteilt`
                  : `Max. 10 · Jeder = ${form.milestones.filter(m => m.t.trim()).length > 0 ? Math.round(100 / form.milestones.filter(m => m.t.trim()).length) : 0}% exp`
                }
              </p>
            )}
          </div>


          {/* Jahresziel-Toggle – nur im Ziele-Tab, nur im Ansichtsmodus */}
          {!isEditing && wizardData?.yearGoal && (
            <div className="mt-2 pt-3 border-t border-slate-100">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Jahresziel · <span className="text-emerald-600">40% EXP</span>
              </p>
              <button
                onClick={toggleWizardYearGoal}
                className={`w-full flex items-center justify-between gap-3 p-3 rounded-xl border transition-all ${
                  wizardData.yearGoal.done
                    ? "bg-emerald-50 border-emerald-300"
                    : "bg-white border-slate-200 hover:border-emerald-200 hover:bg-emerald-50/30"
                }`}
              >
                <span className={`text-sm text-left flex-1 font-medium ${wizardData.yearGoal.done ? "line-through text-slate-400" : "text-slate-700"}`}>
                  {wizardData.yearGoal.number} {wizardData.yearGoal.text}
                </span>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                  wizardData.yearGoal.done ? "bg-emerald-500 border-emerald-500" : "border-slate-300"
                }`}>
                  {wizardData.yearGoal.done && <CheckCircle2 size={14} className="text-white" />}
                </div>
              </button>
            </div>
          )}


          {/* Hindernisse */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">
              Hindernisse und wie man sie überwinden kann
            </label>
            {isEditing ? (
              <>
                <div className="grid grid-cols-2 gap-1 text-xs text-slate-500 mb-1">
                  <span>Hindernis</span>
                  <span>Lösung</span>
                </div>
                <div className="space-y-2">
                  {form.obstacles.map((obs, i) => (
                    <div key={i} className="grid grid-cols-2 gap-2">
                      <Input
                        value={obs.p}
                        onChange={(e) => {
                          const newObs = [...form.obstacles];
                          newObs[i].p = e.target.value;
                          setForm({ ...form, obstacles: newObs });
                        }}
                        placeholder="Hindernis?"
                        className="border-red-200 text-sm"
                      />
                      <Input
                        value={obs.s}
                        onChange={(e) => {
                          const newObs = [...form.obstacles];
                          newObs[i].s = e.target.value;
                          setForm({ ...form, obstacles: newObs });
                        }}
                        placeholder="Lösung?"
                        className="border-green-200 text-sm"
                      />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="space-y-2">
                {form.obstacles.filter(o => o.p.trim() || o.s.trim()).map((obs, i) => (
                  <div key={i} className="grid grid-cols-2 gap-4 text-sm">
                    <div className="text-red-600">• {obs.p || "-"}</div>
                    <div className="text-green-600">→ {obs.s || "-"}</div>
                  </div>
                ))}
                {form.obstacles.filter(o => o.p.trim() || o.s.trim()).length === 0 && (
                  <p className="text-slate-400 italic">-</p>
                )}
              </div>
            )}
          </div>

          {/* Erfolge */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">Erfolge & Fortschritte</label>
              {isEditing && (
                <Button variant="ghost" size="sm" onClick={addSuccess} className="text-green-600 h-7 px-2">
                  <Plus size={14} />
                </Button>
              )}
            </div>
            {isEditing ? (
              form.successes.length === 0 ? (
                <p className="text-sm text-slate-400 italic">Hier dokumentierst du später deine Erfolge...</p>
              ) : (
                <div className="space-y-2">
                  {form.successes.map((success, i) => (
                    <div key={i} className="flex gap-2">
                      <Input
                        value={success}
                        onChange={(e) => {
                          const newSuccesses = [...form.successes];
                          newSuccesses[i] = e.target.value;
                          setForm({ ...form, successes: newSuccesses });
                        }}
                        placeholder="Was hast du erreicht?"
                        className="border-green-200 text-sm"
                      />
                      <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, successes: form.successes.filter((_, idx) => idx !== i) })} className="text-red-500 h-9 px-2">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <ul className="list-disc list-inside text-green-700 space-y-1">
                {form.successes.filter(s => s.trim()).map((success, i) => (
                  <li key={i}>{success}</li>
                ))}
                {form.successes.filter(s => s.trim()).length === 0 && <p className="text-slate-400 italic">Noch keine Erfolge dokumentiert</p>}
              </ul>
            )}
          </div>

          {/* Progress Bottom */}
          <div className="pt-4 border-t border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Fortschritt exp</span>
              <span className="text-lg font-bold text-emerald-600">{progress}%</span>
            </div>
            <div className="bg-slate-100 rounded-full h-5 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-emerald-400 via-teal-500 to-cyan-500 transition-all duration-500 flex items-center justify-end pr-2"
                style={{ width: `${Math.max(progress, 5)}%` }}
              >
                {progress > 20 && <Sparkles size={12} className="text-white" />}
              </div>
            </div>
          </div>

          {/* Action Button */}
          <div className="pt-2">
            {isEditing ? (
              <Button onClick={saveGoal} className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white">
                Speichern
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button onClick={() => setIsEditing(true)} variant="outline" className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                  <Pencil size={16} className="mr-2" />
                  Bearbeiten
                </Button>
                <Button onClick={handleNewGoal} variant="outline" className="flex-1 border-slate-300 text-slate-600 hover:bg-slate-50 text-xs">
                  Neues Ziel
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Milestone Modal */}
      <Dialog open={showMilestoneModal} onOpenChange={setShowMilestoneModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="text-emerald-500" />
              Meilensteine abhaken
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {/* Wochenziel – immer anzeigen wenn Wizard weekGoals hat */}
            {wizardData?.weekGoals && (() => {
              const weekKey = getCurrentWeekKey();
              const entry = wizardData.weekGoals[weekKey];
              if (!entry) return null;
              const doneWeeks = Object.values(wizardData.weekGoals).filter(w => w.done).length;
              const totalWeeks = Object.keys(wizardData.weekGoals).length || 52;
              return (
                <div className="mb-1 pb-3 border-b border-slate-200">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    Wochenziel · {doneWeeks} von {totalWeeks} Wochen erreicht
                  </p>
                  <button
                    onClick={toggleWizardWeek}
                    className={`w-full flex items-center justify-between gap-3 p-3 rounded-lg border transition-all ${
                      entry.done
                        ? "bg-emerald-50 border-emerald-300"
                        : "bg-white border-slate-200 active:bg-slate-50"
                    }`}
                  >
                    <span className={`text-sm text-left flex-1 ${entry.done ? "line-through text-slate-400" : "text-slate-700"}`}>
                      KW {weekKey.split("-W")[1]}: {entry.number} {entry.text}
                    </span>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      entry.done ? "bg-emerald-500 border-emerald-500" : "border-slate-300"
                    }`}>
                      {entry.done && <CheckCircle2 size={14} className="text-white" />}
                    </div>
                  </button>
                </div>
              );
            })()}

            {/* Meilensteine – Wizard-Meilenstein wird hier nicht angezeigt (nur als Jahresziel in Ziele-Tab) */}
            {form.milestones.filter(m => m.t.trim() && m.t !== wizardData?.wizardMilestoneText).length === 0 ? (
              <p className="text-slate-500 text-center py-4">Keine Meilensteine definiert</p>
            ) : (
              form.milestones.filter(m => m.t.trim() && m.t !== wizardData?.wizardMilestoneText).map((milestone, i) => {
                return (
                  <button
                    key={i}
                    onClick={() => toggleMilestone(form.milestones.findIndex(m => m.t === milestone.t))}
                    className={`w-full flex items-center justify-between gap-3 p-3 rounded-lg border transition-all ${
                      milestone.d
                        ? "bg-green-50 border-green-300"
                        : "bg-white border-slate-200 active:bg-slate-50"
                    }`}
                  >
                    <span className={`text-sm text-left flex-1 ${milestone.d ? "line-through text-slate-400" : "text-slate-700"}`}>
                      {milestone.t}
                    </span>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      milestone.d
                        ? "bg-green-500 border-green-500"
                        : "border-slate-300"
                    }`}>
                      {milestone.d && <CheckCircle2 size={14} className="text-white" />}
                    </div>
                  </button>
                );
              })
            )}
          </div>
          <div className="flex items-center gap-3 pt-2 border-t">
            {/* Fortschrittsleiste: Wizard-Meilenstein (grün, 40%) + Meilensteine (blau, 60%) */}
            <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden flex">
              {wizardData?.wizardMilestoneText && (() => {
                const validMs = form.milestones.filter(m => m.t.trim());
                const others = validMs.filter(m => m.t !== wizardData.wizardMilestoneText);
                const wizFrac = getWizardFraction();
                if (others.length === 0) {
                  return <div className="h-full bg-emerald-400 transition-all duration-500 rounded-full" style={{ width: `${wizFrac * 100}%` }} />;
                }
                const wizWidth = wizFrac * 40;
                const otherFrac = others.filter(m => m.d).length / others.length;
                const msWidth = otherFrac * 60;
                return (
                  <>
                    <div className="h-full bg-emerald-400 transition-all duration-500" style={{ width: `${wizWidth}%` }} title={`Jahresziel: ${Math.round(wizWidth)}%`} />
                    <div className="h-full bg-gradient-to-r from-teal-400 to-blue-500 transition-all duration-500" style={{ width: `${msWidth}%` }} title={`Meilensteine: ${Math.round(msWidth)}%`} />
                  </>
                );
              })()}
              {wizardData?.weekGoals && !wizardData?.wizardMilestoneText && (() => {
                const totalWeeks = Object.keys(wizardData.weekGoals).length || 52;
                const doneWeeks = Object.values(wizardData.weekGoals).filter(w => w.done).length;
                const wizardFrac = wizardData.yearGoal?.done ? 1 : doneWeeks / totalWeeks;
                const validMs = form.milestones.filter(m => m.t.trim());
                const msFrac = validMs.length === 0 ? 0 : validMs.filter(m => m.d).length / validMs.length;
                const wizardWidth = wizardFrac * 40;
                const msWidth = msFrac * 60;
                return (
                  <>
                    <div className="h-full bg-emerald-400 transition-all duration-500" style={{ width: `${wizardWidth}%` }} title={`Wochenziele: ${Math.round(wizardWidth)}%`} />
                    <div className="h-full bg-gradient-to-r from-teal-400 to-blue-500 transition-all duration-500" style={{ width: `${msWidth}%` }} title={`Meilensteine: ${Math.round(msWidth)}%`} />
                  </>
                );
              })()}
              {!wizardData?.weekGoals && !wizardData?.wizardMilestoneText && (
                <div className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
              )}
            </div>
            <span className="text-sm font-bold text-emerald-600">{progress}%</span>
          </div>
        </DialogContent>
      </Dialog>

      {/* Congratulations Modal – fancy mit Feuerwerk & Sound */}
      <CelebrationModal
        open={showCongratulations}
        onClose={() => setShowCongratulations(false)}
        goalTitle={form.title}
        onNavigateTo={onNavigateTo}
      />
    </div>
  );
}
